import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogType,
    ConfirmationDialogOption,
} from '@components/confirmation-dialog/confirmation-dialog.component';
import { Model, VesselEquipment, DefaultDevices } from 'icreate/common/models/device';
import { fromEvent, lastValueFrom, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-single-device-list',
    templateUrl: './single-device-list.component.html',
    styleUrl: './single-device-list.component.css',
    standalone: false,
})
export class SingleDeviceListComponent implements OnInit, OnDestroy {
    @Input() models: Model[];
    @Input() vesselEquipment: VesselEquipment[];
    @Input() initialVesselEquipment: VesselEquipment[];
    @Input() vesselId: string;
    @Input() deviceType: DefaultDevices;
    @ViewChild('optionsCol') optionsCol: ElementRef;
    @Output() onVesselEquipmentChanged: EventEmitter<VesselEquipment[]> = new EventEmitter();

    isDescriptionExpanded = false;

    selectedModel: Model;
    isOptionsOpen: boolean = false;
    optionsTop: string;

    private destroy$: Subject<void> = new Subject();

    constructor(private dialog: MatDialog) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        if (this.isOptionsOpen && !this.optionsCol?.nativeElement.contains(event.target)) {
            this.isOptionsOpen = false;
        }
    }

    ngOnInit(): void {
        const scrollContainer = document.querySelector('.system-setting-scroll-parent');
        if (scrollContainer) {
            fromEvent(scrollContainer, 'scroll')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.isOptionsOpen = false;
                });
        }
        if (!this.deviceType) return null;
        if (!this.vesselEquipment || this.vesselEquipment.length == 0) return null;
        if (!this.models && this.models.length == 0) return null;
        const selectedEquipment = this.vesselEquipment.find(equipment => equipment.name == this.deviceType);

        if (!selectedEquipment) return null;
        this.selectedModel = this.models.find(model => model.id == selectedEquipment.equipmentModelId);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async deleteUnit() {
        const unitIndex = this.vesselEquipment.findIndex(equipment => equipment.name == this.deviceType);
        if (unitIndex >= 0) {
            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                width: '415px',
                data: {
                    type: ConfirmationDialogType.SIMPLE,
                    title: 'device-list.delete.title',
                    title2: 'device-list.delete.title2',
                    discardButton: 'dlg.cancel',
                    saveButton: 'dlg.confirm',
                    width: '452px',
                },
                disableClose: true,
            });
            const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

            if (confirmationOption == ConfirmationDialogOption.SAVE) {
                if (this.vesselEquipment && this.vesselEquipment.length > 0) {
                    this.vesselEquipment.splice(unitIndex, 1);
                }
                this.selectedModel = null;
                this.onVesselEquipmentChanged.emit(this.vesselEquipment);
            }
        }
    }

    handleModelSelected(selectedModel: Model) {
        if (this.vesselEquipment && this.vesselEquipment.length > 0) {
            const unitIndex = this.vesselEquipment.findIndex(equipment => equipment.name == this.deviceType);
            if (unitIndex >= 0) {
                this.vesselEquipment[unitIndex].equipmentModelId = selectedModel.id;
                return;
            }
        } else this.vesselEquipment = [];

        if (this.initialVesselEquipment && this.initialVesselEquipment.length > 0) {
            const initialEquipment = this.initialVesselEquipment.find(equipment => equipment.name == this.deviceType);
            if (initialEquipment) {
                this.vesselEquipment.push({
                    ...initialEquipment,
                    equipmentModelId: selectedModel.id,
                });
                this.selectedModel = selectedModel;
                this.isDescriptionExpanded = false;
                this.onVesselEquipmentChanged.emit(this.vesselEquipment);
                return;
            }
        }

        this.vesselEquipment.push({
            name: this.deviceType,
            equipmentModelId: selectedModel.id,
            vesselId: this.vesselId,
        });

        this.selectedModel = selectedModel;
        this.isDescriptionExpanded = false;
        this.onVesselEquipmentChanged.emit(this.vesselEquipment);
    }

    updatePosition() {
        if (!this.optionsCol) return;
        const optionsPosition = this.optionsCol.nativeElement.getBoundingClientRect();
        const scrollTop = window.scrollY;

        this.optionsTop = optionsPosition.bottom + scrollTop + 'px';
    }
}
